import {Component, HostListener, Inject, OnInit, Optional} from '@angular/core';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {CommunicationCenterService} from '@modules/communication-center';
import {DataEntity} from 'octopus-connect';
import {combineLatest, Observable, of} from 'rxjs';
import {map, take, tap} from 'rxjs/operators';

import {IGamecodeFormOptions} from '@modules/gamecode/core/gamecode.service';
import {get} from 'lodash-es';

interface IGamecodeDataInterface {
    title: string;
    associatedLessonId?: string | number;
}

interface ModalDataInterface {
    availableLessons$: Observable<DataEntity[]>;
    defaultValues: IGamecodeDataInterface;
    getLessonById: (lessonId: string | number) => Observable<DataEntity>;
    options: IGamecodeFormOptions;
    saveGamecode: (data: IGamecodeDataInterface) => Observable<DataEntity>;
}

@Component({
    selector: 'app-gamecode-data-editor-modal',
    templateUrl: './gamecode-data-editor-modal.component.html',
    standalone: false
})
export class GamecodeDataEditorModalComponent implements OnInit {
    /**
     * Define if the component is currently loaded for initialization
     */
    public isInitializing = true;
    /**
     * Define if the component is currently in saving (should be used for temporally disable component)
     */
    public isSaving = false;

    /**
     * List of available lessons (not associated lessons and current note already associated lesson)
     */
    public lessons: DataEntity[] = [];

    /**
     * Reactive form used to receive and control user inputs
     */
    public noteForm: UntypedFormGroup;

    /**
     * Used to set or get the associated lesson
     */
    public selectedLesson: DataEntity = null;
    public availableLessonsAreLoading = true;

    public get lessonIsDisabled(): boolean {
        return this.isSaving || <boolean>get(this.modalData.options, 'associatedLessonId.disable', false);
    }

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) private modalData: ModalDataInterface,
        private communicationCenter: CommunicationCenterService,
        private dialog: MatDialog,
        private formBuilder: UntypedFormBuilder,
        public selfDialogRef: MatDialogRef<GamecodeDataEditorModalComponent>
    ) {
    }

    ngOnInit(): void {
        this.noteForm = this.formBuilder.group({
            title: [get(this.modalData, 'defaultValues.title', ''), Validators.required],
            application: [get(this.modalData, 'defaultValues.application', ''), Validators.required],
        });

        this.isInitializing = false;
    }

    /**
     * Save the gamecode. No need to know if it's a path or a creation.
     * @param value
     */
    public onSubmit(value: IGamecodeDataInterface): void {
        if (this.isSaving) {
            return;
        }
        this.isSaving = true;
        this.noteForm.disable();

        this.modalData.saveGamecode(value).pipe(
            tap((dataEntity) => {
                this.selfDialogRef.close(dataEntity);
            }),
            take(1)
        ).subscribe();
    }
}